/**
 * Which of the plan's sessions is TODAY's workout — PURE, so the rule has one
 * home that the readiness screen (T54) and Today's hero (T60) both read.
 *
 * The plan (`GET /me/plan`, `planView.sessions`) says what each session is
 * and, for a fixed week, on which weekday it falls (`weekday`, 0 = Sunday,
 * `null` for a variable week). The history (`GET /me/sessions`, plus the
 * session on this device that may not have synced yet) says what was done.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 * 1. A session fixed to today's weekday that has not been trained today.
 * 2. Otherwise the rotation: the session after the one trained most recently,
 *    by `position`, wrapping to the first — skipping any already trained
 *    today, unless every one was. This is also a fixed week's answer on a day
 *    it did not plan: "the next one", never nothing.
 * 3. With no history at all, the first session by `position`.
 *
 * A day with NO exercise is never today's workout, by any of the three: a
 * day is added empty and filled by hand (`D197`), and an empty D after C
 * once made Today say the plan had no workout while A, B and C were full.
 * The rotation walks past it; with no day holding an exercise the answer is
 * `null`. The server's `nextPlanSessionId` (`insight.ts`) walks the same way.
 *
 * An ABANDONED session is not a workout (`D177`): it neither moves the
 * rotation nor counts as trained today, so after an abandoned B today is B
 * again — the rule Today's insight applies on the server
 * (`@repo/training`'s `insight.ts`). An early ending and an open session
 * still count, as they do there.
 *
 * A day chosen by hand (the plan screen's "Treinar este hoje", which opens
 * the workout with `day=`) overrides the whole rule — `chooseSession` — and
 * then moves the rotation like any other workout: after training C out of
 * turn, the next is D, as the server's `nextPlanSessionId` computes it. A
 * fixed-weekday session still wins the DEFAULT choice; the request only
 * overrides it when it names a session of this plan.
 *
 * Wrapping is deliberate here, where the server's `nextPlanSessionId` does
 * not wrap (`routes/workout.ts`, "null when the plan is finished"): the
 * summary reports the end of a cycle, while a person who taps "Treinar"
 * after the last session wants the first one again, not an empty screen.
 *
 * Days are LOCAL days of `now`: a workout at 23:30 belongs to the evening it
 * was done in, not to the UTC date.
 */

import type { WorkoutSessionView } from "@repo/contract/training";

export interface ChoosableSession {
  /** The day's lines; an empty day is never chosen. Absent: not known, so
   *  the day counts as one with exercises. */
  readonly items?: readonly unknown[];
  readonly position: number;
  readonly sessionId: string;
  readonly weekday: number | null;
}

/** Whether a day can be trained: it has an exercise, or its lines are not known. */
function trainable(session: ChoosableSession): boolean {
  return session.items === undefined || session.items.length > 0;
}

export interface TrainedSession {
  /**
   * `true` when the session was ended as abandoned (`D177`), from the list
   * item's flag. Absent means not abandoned — what an older server, and the
   * device's own session, always send.
   */
  readonly abandoned?: true;
  /** `null` for a session started off-plan; it never names a plan session. */
  readonly planSessionId: string | null;
  /**
   * The workout session's own id, which the device's copy shares with the
   * list (the app mints it and the start sends it). What `historyWith` matches
   * the two on; absent, an entry is never matched and always counts.
   */
  readonly sessionId?: string;
  /** ISO datetime (the wire) or epoch ms (the device's own session). */
  readonly startedAt: string | number;
}

/**
 * A `GET /me/sessions` item as the rotation reads it. The one mapper both
 * callers use, through `rotationHistoryOf`, so neither can copy the plan
 * session and the start and drop the abandoned flag — which is how an
 * abandoned B once moved Today to C.
 */
export function trainedSessionOf(
  session: Pick<
    WorkoutSessionView,
    "abandoned" | "planSessionId" | "sessionId" | "startedAt"
  >
): TrainedSession {
  return {
    planSessionId: session.planSessionId,
    sessionId: session.sessionId,
    startedAt: session.startedAt,
    ...(session.abandoned === true ? { abandoned: true as const } : {}),
  };
}

/**
 * The server's history plus the device's own session. The device's copy is
 * appended only when the list does not already hold that session: an
 * unsynced start still counts, but once the server knows the session its
 * entry wins, flag included. The device never abandons its own session, yet
 * another device can, and so can the server's six-hour auto-close (`D177`) —
 * and a copy still persisted here, carrying no flag, would otherwise count
 * that abandoned B as trained and move Today on to C.
 */
export function historyWith(
  remote: readonly TrainedSession[],
  local: TrainedSession | null
): TrainedSession[] {
  if (
    local === null ||
    (local.sessionId !== undefined &&
      remote.some((entry) => entry.sessionId === local.sessionId))
  ) {
    return [...remote];
  }
  return [...remote, local];
}

/**
 * The rotation's history, as both callers build it: every `GET /me/sessions`
 * item through `trainedSessionOf`, then the device's own session through
 * `historyWith` (`null` where the caller has none). Today's "Treinar" and
 * search's start action call this rather than mapping the list themselves,
 * so the test that pins this pins them. An inline map that copies the plan
 * session and the start type-checks — the flag is optional — and drops the
 * abandoned flag (`D177`): the app offers C while the server says B.
 */
export function rotationHistoryOf(
  items: readonly Pick<
    WorkoutSessionView,
    "abandoned" | "planSessionId" | "sessionId" | "startedAt"
  >[],
  local: TrainedSession | null
): TrainedSession[] {
  return historyWith(items.map(trainedSessionOf), local);
}

export type TodaysSessionReason =
  | "weekday"
  | "rotation"
  | "first"
  | "requested";

export interface TodaysSession<S extends ChoosableSession> {
  readonly reason: TodaysSessionReason;
  readonly session: S;
}

function localDay(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function timeOf(entry: TrainedSession): number {
  return typeof entry.startedAt === "number"
    ? entry.startedAt
    : Date.parse(entry.startedAt);
}

export function chooseTodaysSession<S extends ChoosableSession>(input: {
  readonly history: readonly TrainedSession[];
  readonly now: Date;
  readonly sessions: readonly S[];
}): TodaysSession<S> | null {
  const all = [...input.sessions].sort((a, b) => a.position - b.position);
  // The history may name an empty day (trained before it was emptied); it
  // still places the rotation, so the walk starts from it over `all`.
  const sorted = all.filter(trainable);
  if (sorted.length === 0) {
    return null;
  }
  const ids = new Set(all.map((s) => s.sessionId));
  const today = localDay(input.now);
  const ofPlan = input.history.filter(
    (entry) =>
      entry.abandoned !== true &&
      entry.planSessionId !== null &&
      ids.has(entry.planSessionId) &&
      Number.isFinite(timeOf(entry))
  );
  const trainedToday = new Set(
    ofPlan
      .filter((entry) => localDay(new Date(timeOf(entry))) === today)
      .map((entry) => entry.planSessionId)
  );

  const weekday = input.now.getDay();
  const fixed = sorted.find(
    (s) => s.weekday === weekday && !trainedToday.has(s.sessionId)
  );
  if (fixed !== undefined) {
    return { reason: "weekday", session: fixed };
  }

  let last: TrainedSession | null = null;
  for (const entry of ofPlan) {
    if (last === null || timeOf(entry) > timeOf(last)) {
      last = entry;
    }
  }
  if (last === null) {
    return { reason: "first", session: sorted[0] as S };
  }
  const from = all.findIndex((s) => s.sessionId === last.planSessionId);
  const rotation = all
    .map((_, k) => all[(from + 1 + k) % all.length] as S)
    .filter(trainable);
  const open = rotation.find((s) => !trainedToday.has(s.sessionId));
  return { reason: "rotation", session: open ?? (rotation[0] as S) };
}

/**
 * The session to start: the one the person asked for (`day=`) when the plan
 * still has it, otherwise today's rule unchanged. An id the plan no longer
 * holds — a stale link, a day removed since — falls back to today's rather
 * than to nothing.
 */
export function chooseSession<S extends ChoosableSession>(
  input: {
    readonly history: readonly TrainedSession[];
    readonly now: Date;
    readonly sessions: readonly S[];
  },
  requestedId: string | undefined
): TodaysSession<S> | null {
  const requested =
    requestedId === undefined
      ? undefined
      : input.sessions.find((s) => s.sessionId === requestedId);
  if (requested !== undefined) {
    return { reason: "requested", session: requested };
  }
  return chooseTodaysSession(input);
}
